import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

// ============= TIPOS =============

export type JobType = 
  // === CRON JOBS ===
  | 'winback' 
  | 'weekly-digest'
  | 'daily-digest'        // Mantido (desativado no cron)
  | 'subscription-alerts'
  // === DATABASE TRIGGERS ===
  | 'profile-complete'
  | 'new-like'
  | 'new-match'
  | 'new-message'
  | 'verification-submitted'
  | 'verification-approved'
  | 'verification-rejected'
  | 'boost-expired'
  | 'welcome'
  // === WEBHOOKS ===
  | 'subscription-confirmed'
  // === PREVIEW ONLY ===
  | 'template-preview';

export type JobCategory = 'cron' | 'trigger' | 'webhook';
export type PlaygroundAction = 'simulate' | 'send_now' | 'schedule_original';
export type PlaygroundMode = 'automations' | 'templates';

export interface EligibilityCheck {
  name: string;
  passed: boolean;
  details?: string;
}

export interface PlaygroundResult {
  eligible: boolean;
  reason?: string;
  checks: EligibilityCheck[];
  templateKey?: string;
  variables?: Record<string, string>;
  originalScheduledFor?: string;
  previewHtml?: string;
  scheduled?: boolean;
  scheduledId?: string;
  scheduledFor?: string;
  error?: string;
}

export interface PlaygroundUser {
  id: string;
  username: string;
  email?: string;
  plan_type: string;
}

export interface JobOption {
  value: JobType;
  label: string;
  description: string;
  category: JobCategory;
  requiresSourceUser?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

// ============= JOB OPTIONS =============

export const JOB_OPTIONS: JobOption[] = [
  // === CRON JOBS ===
  { 
    value: 'winback', 
    label: 'Winback', 
    description: 'Usuários inativos (3, 6, 9, 12 dias)',
    category: 'cron'
  },
  { 
    value: 'weekly-digest', 
    label: 'Weekly Digest', 
    description: 'Resumo semanal de atividade (domingos)',
    category: 'cron'
  },
  { 
    value: 'daily-digest', 
    label: 'Daily Digest', 
    description: '⚠️ DESATIVADO - Substituído por Weekly Digest',
    category: 'cron',
    disabled: true,
    disabledReason: 'Substituído por Weekly Digest'
  },
  { 
    value: 'subscription-alerts', 
    label: 'Alertas de Assinatura', 
    description: 'Avisos de expiração de plano (7, 3, 1 dias)',
    category: 'cron'
  },
  
  // === DATABASE TRIGGERS ===
  { 
    value: 'profile-complete', 
    label: 'Perfil Completo', 
    description: 'Quando completa onboarding',
    category: 'trigger'
  },
  { 
    value: 'new-like', 
    label: 'Nova Curtida', 
    description: 'Quando recebe uma curtida',
    category: 'trigger',
    requiresSourceUser: true
  },
  { 
    value: 'new-match', 
    label: 'Novo Match', 
    description: 'Quando há match mútuo',
    category: 'trigger',
    requiresSourceUser: true
  },
  { 
    value: 'new-message', 
    label: 'Nova Mensagem', 
    description: 'Quando recebe mensagem (offline)',
    category: 'trigger',
    requiresSourceUser: true
  },
  { 
    value: 'verification-submitted', 
    label: 'Verificação Enviada', 
    description: 'Quando envia selfie para verificação',
    category: 'trigger'
  },
  { 
    value: 'verification-approved', 
    label: 'Verificação Aprovada', 
    description: 'Quando aprovado pelo admin',
    category: 'trigger'
  },
  { 
    value: 'verification-rejected', 
    label: 'Verificação Rejeitada', 
    description: 'Quando rejeitado pelo admin',
    category: 'trigger'
  },
  { 
    value: 'boost-expired', 
    label: 'Boost Expirou', 
    description: 'Quando boost do usuário expira',
    category: 'trigger'
  },
  { 
    value: 'welcome', 
    label: 'Boas-Vindas', 
    description: 'Após confirmação de email',
    category: 'trigger'
  },
  
  // === WEBHOOKS ===
  { 
    value: 'subscription-confirmed', 
    label: 'Assinatura Confirmada', 
    description: 'Após pagamento confirmado (Asaas)',
    category: 'webhook'
  },
];

// Helper to get jobs by category
export const getJobsByCategory = (category: JobCategory): JobOption[] => {
  return JOB_OPTIONS.filter(job => job.category === category);
};

// ============= HOOK =============

export function useEmailPlayground() {
  const [mode, setMode] = useState<PlaygroundMode>('automations');
  const [selectedJob, setSelectedJob] = useState<JobType | ''>('');
  const [selectedUser, setSelectedUser] = useState<PlaygroundUser | null>(null);
  const [sourceUser, setSourceUser] = useState<PlaygroundUser | null>(null);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('');
  const [customVariables, setCustomVariables] = useState<Record<string, string>>({});
  const [result, setResult] = useState<PlaygroundResult | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);

  const simulate = async () => {
    if (mode === 'automations') {
      if (!selectedJob || !selectedUser) {
        toast.error('Selecione uma automação e um usuário');
        return;
      }
      
      // Check if job requires source user
      const jobInfo = JOB_OPTIONS.find(j => j.value === selectedJob);
      if (jobInfo?.requiresSourceUser && !sourceUser) {
        toast.error('Selecione o usuário fonte (quem realizou a ação)');
        return;
      }
    } else {
      if (!selectedTemplateKey || !selectedUser) {
        toast.error('Selecione um template e um usuário');
        return;
      }
    }

    setIsSimulating(true);
    setResult(null);

    try {
      const body = mode === 'automations' 
        ? {
            jobType: selectedJob,
            userId: selectedUser!.id,
            sourceUserId: sourceUser?.id,
            action: 'simulate',
          }
        : {
            jobType: 'template-preview',
            userId: selectedUser!.id,
            templateKey: selectedTemplateKey,
            customVariables,
            action: 'simulate',
          };

      const { data, error } = await supabase.functions.invoke('email-playground', {
        body,
      });

      if (error) {
        throw new Error(error.message);
      }

      setResult(data);
      
      if (data.eligible) {
        toast.success(mode === 'automations' ? 'Usuário elegível para este email' : 'Preview gerado com sucesso');
      } else {
        toast.info(`Não elegível: ${data.reason}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      toast.error(`Erro ao simular: ${errorMessage}`);
      setResult({ eligible: false, reason: errorMessage, checks: [] });
    } finally {
      setIsSimulating(false);
    }
  };

  const scheduleEmail = async (action: 'send_now' | 'schedule_original') => {
    if (!selectedUser) {
      toast.error('Selecione um usuário');
      return;
    }

    if (mode === 'automations' && !selectedJob) {
      toast.error('Selecione uma automação');
      return;
    }

    if (mode === 'templates' && !selectedTemplateKey) {
      toast.error('Selecione um template');
      return;
    }

    setIsScheduling(true);

    try {
      const body = mode === 'automations'
        ? {
            jobType: selectedJob,
            userId: selectedUser.id,
            sourceUserId: sourceUser?.id,
            action,
          }
        : {
            jobType: 'template-preview',
            userId: selectedUser.id,
            templateKey: selectedTemplateKey,
            customVariables,
            action,
          };

      const { data, error } = await supabase.functions.invoke('email-playground', {
        body,
      });

      if (error) {
        throw new Error(error.message);
      }

      if (data.scheduled) {
        setResult(data);
        toast.success(
          action === 'send_now' 
            ? 'Email enviado para processamento imediato!' 
            : 'Email agendado com sucesso!'
        );
      } else {
        toast.error(data.error || 'Falha ao agendar email');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      toast.error(`Erro ao agendar: ${errorMessage}`);
    } finally {
      setIsScheduling(false);
    }
  };

  const reset = () => {
    setResult(null);
    setCustomVariables({});
  };

  const updateCustomVariable = (key: string, value: string) => {
    setCustomVariables(prev => ({ ...prev, [key]: value }));
  };

  return {
    mode,
    setMode,
    selectedJob,
    setSelectedJob,
    selectedUser,
    setSelectedUser,
    sourceUser,
    setSourceUser,
    selectedTemplateKey,
    setSelectedTemplateKey,
    customVariables,
    setCustomVariables,
    updateCustomVariable,
    result,
    isSimulating,
    isScheduling,
    simulate,
    scheduleEmail,
    reset,
  };
}
